import type { ReactNode } from 'react';

import {
  ANDROID_FROST_FILL_CLASS,
  GLASS_BLUR_INTENSITY,
  glassBlurTint,
} from '../../styles/glass';
import { useTheme } from '../../theme';
import { GlassBackground } from './GlassBackground';

export interface OverlayGlassProps {
  /**
   * Corner radius (px) the frost clips to — the INNER edge of the surface's
   * border, where it has one.
   */
  radius?: number;
  /** Frost strength. Defaults to the panel recipe in `styles/glass.ts`. */
  intensity?: number;
  /**
   * Painted instead of the frost where it cannot render: Android below SDK 31,
   * or an app that never mounted a `GlassBlurTarget`.
   *
   * Required, and deliberately not defaulted: a translucent overlay with no
   * frost behind it is a dimmed window, not glass, and each overlay knows the
   * opaque-enough fill its own surface uses.
   */
  fallbackClassName?: string;
  /** Extra classes for the clip wrapper. */
  className?: string;
  /** Painted between frost and fill — see `GlassBackgroundProps.scrim`. */
  scrim?: ReactNode;
  /** Static form of {@link OverlayGlassProps.scrim}. */
  scrimOpacity?: number;
  /**
   * `false` for an overlay that renders INSIDE the app's `GlassBlurTarget`
   * (an in-tree portal host, an inline anchored box) rather than in its own
   * RN `Modal` window. Android's frost DRAWS the target tree, so a layer that
   * is part of that tree draws itself, recursively — a hard `SIGSEGV` in
   * `libhwui` (`RenderNode::prepareTreeImpl` / `computeTransformImpl`), not a
   * cosmetic ghost. Such an overlay paints `fallbackClassName` instead.
   * Default `true`: the Modal-hosted menus and popovers frost.
   */
  frostOnAndroid?: boolean;
}

/**
 * Frosted glass for a DS OVERLAY — a menu, tooltip, popover or toast that
 * floats over the page.
 *
 * The one place the overlay recipe lives, because the three surfaces that need
 * it kept drifting apart: each had picked its own intensity, its own Android
 * fill, and its own answer to "can Android frost here at all". They are the
 * same surface wearing different shapes, so they get one component.
 *
 * ANDROID FROSTS HERE, which corrects the assumption these overlays shipped
 * with. Anchored overlays present inside a React Native `Modal`, and Android
 * puts a `Modal` in its own window — from which it was concluded that
 * expo-blur had nothing to sample. The window boundary turns out not to
 * matter: the Dimezis backend blurs by DRAWING the nominated
 * `GlassBlurTarget` view tree into its own canvas, and a view in another
 * window is still an attached, drawable view. Re-measured on a Pixel 10
 * emulator (API 37): the Create menu goes from 2.9% to 53.6% soft content once
 * the frost is on — i.e. it blurs, the way iOS always did.
 *
 * iOS is unchanged: a `UIVisualEffectView` samples whatever is beneath it.
 */
export function OverlayGlass({
  radius,
  intensity = GLASS_BLUR_INTENSITY,
  fallbackClassName,
  className,
  scrim,
  scrimOpacity,
  frostOnAndroid = true,
}: OverlayGlassProps) {
  const { isDark } = useTheme();
  return (
    <GlassBackground
      intensity={intensity}
      tint={glassBlurTint(isDark)}
      radius={radius}
      // Android re-paints the surface tint ABOVE the frost (Dimezis covers the
      // fill behind it); iOS ignores this and samples the fill instead.
      fillClassName={ANDROID_FROST_FILL_CLASS}
      fallbackClassName={fallbackClassName}
      frostOnAndroid={frostOnAndroid}
      className={className}
      scrim={scrim}
      scrimOpacity={scrimOpacity}
    />
  );
}
OverlayGlass.displayName = 'OverlayGlass';
